import React, { useState, useEffect } from 'react';
import { apiFetch, resolveAssetUrl } from '../../api/client';
import {
  Hotel,
  LayoutDashboard,
  CalendarCheck,
  DoorOpen,
  CircleDollarSign,
  ShieldCheck,
  Package,
  FileBarChart,
  Users,
  UserCog,
  LogOut,
  BedDouble,
  TrendingUp,
  Settings as SettingsIcon,
  Landmark,
  FileText,
  FolderArchive,
  Truck,
  Target,
  Droplets,
  Sparkles,
  ChefHat,
  Wallet,
  MessagesSquare,
  Utensils,
  Presentation,
  QrCode,
  Armchair,
  Smartphone,
  CalendarDays,
  Receipt,
  Building2,
  Contact,
  ScanLine,
  HeartPulse,
  ClipboardList,
  Boxes,
  ClipboardCheck,
  ShieldAlert,
  Fuel,
} from 'lucide-react';

import TopNavBar from '../../components/TopNavBar';
import NavDropdown from '../../components/NavDropdown';
import UserMenu from '../../components/UserMenu';
import NotificationBell from '../../components/NotificationBell';
import ShiftClockWidget from '../../components/ShiftClockWidget';
import ImpersonationBanner from '../../components/ImpersonationBanner';
import PlatformAnnouncementBanner from '../../components/PlatformAnnouncementBanner';
import GuestAlertBanner from '../../components/GuestAlertBanner';
import PanelDesktop from '../../components/panels/PanelDesktop';
import PanelHost from '../../components/panels/PanelHost';
import { canSeeModule } from '../../config/roleModules';
import { MODULE_REGISTRY, MODULE_ICONS, PRELOAD_KEYS, getModuleEntry } from '../../config/moduleRegistry';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useRoleCapabilities } from '../../hooks/useRoleCapabilities';
import { useUnreadChat } from '../../hooks/useUnreadChat';
import { usePanelManager } from '../../hooks/usePanelManager';
import '../../polish.css';
import './DashboardHome.css';
import { frontDeskSummary, localToday } from './FrontDeskPanel';

function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showActiveBookings, setShowActiveBookings] = useState(false);
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);
  const [revenueDetails, setRevenueDetails] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [idPreview,setIdPreview] = useState('');
  const [showGuestDetails, setShowGuestDetails] = useState(false);
  const [tenantBrand, setTenantBrand] = useState(null); // { name, logo_url } | null
  const { isEnabled: isFeatureEnabled } = useEntitlements();
  // Per-tenant role capability overrides (Role Management, 2026-09-26) —
  // see roleModules.js's MODULE_CAPABILITY/canSeeModule for why this is a
  // 4th, additive-only check alongside the static MODULE_ROLES array.
  const { hasCapability: hasRoleCapability, title: myTitle, label: myRoleLabel } = useRoleCapabilities();

  useEffect(() => {
    const loadTenant = async () => {
      try {
        const res = await apiFetch('/tenants/me');
        if (res.ok) {
          const data = await res.json();
          if (data.success) setTenantBrand(data.tenant);
        }
      } catch (err) {
        console.error('Failed to load tenant branding:', err);
      }
    };
    loadTenant();
  }, []);

  // MDI panel manager — replaces the old single-activeTab model. See the
  // project plan (mutable-tinkering-kernighan, TALIT PMS: MDI Dashboard)
  // for the full design writeup. Every module now opens as its own
  // floating panel that stays open instead of replacing whatever was on
  // screen; usePanelManager (frontend/src/hooks/usePanelManager.js) owns
  // all open-panel state and persists it to localStorage per logged-in
  // user.
  const {
    panels, zOrder, openOrFocusPanel, openNewInstance, focusPanel,
    closePanel, minimizePanel, restorePanel, toggleMaximize, bringToFront,
    movePanel, resizePanel,
  } = usePanelManager({ userId: user?.id, moduleRegistry: MODULE_REGISTRY });

  // A module is "active" for polling-pause purposes (see the useUnreadChat
  // call below) if ANY panel for it is currently open — matches the old
  // activeTab !== 'chat' intent (don't toast/badge for messages you already
  // have open), extended to "open at all," not just "the frontmost panel,"
  // since a backgrounded Chat panel is still a Chat panel you're looking at
  // peripherally, same reasoning as KDS/TablesPOS's own polling-pause rule.
  const hasOpenPanelFor = (moduleKey) =>
    zOrder.some((id) => panels[id]?.moduleKey === moduleKey);

  // On mount: if the URL names a module (from a right-click "open in new
  // tab" link, or a bookmarked/shared deep link), open-or-focus that
  // panel. This subsumes the old hash-based getInitialTab entirely — the
  // hash's only remaining job is "what to open when a link is opened,"
  // not "mirror current state" (localStorage, via usePanelManager, is now
  // the source of truth for reload continuity). Runs once on mount only;
  // intentionally has no dependency on `panels`/`zOrder` themselves so it
  // never re-fires as panels open/close during normal use.
  // waiter/chef have no home dashboard (see DashboardContent's own render
  // gate below) — the panel-desktop area behind whatever's open would
  // otherwise be genuinely blank on first login (nothing persisted yet in
  // localStorage). Auto-focus a sensible first panel for each role instead
  // of shipping an empty screen. Only fires when nothing is already open
  // (a returning user's restored layout is left alone), and only once on
  // mount — same "no dependency on panels/zOrder" reasoning as the
  // hash-open effect above, so it never re-fires as panels open/close.
  useEffect(() => {
    if (zOrder.length > 0) return;
    if (user?.role === 'waiter') openOrFocusPanel('tables');
    else if (user?.role === 'chef') openOrFocusPanel('recipes');
    // cashier (2026-09-21, explicit user decision): lands on the quick-sale
    // till rather than Tables — POS is their more primary screen, Tables is
    // mainly for confirming a waiter's pending cash handoff, not where a
    // shift typically starts.
    else if (user?.role === 'cashier') openOrFocusPanel('pos');
    // housekeeping DOES keep the home dashboard (unlike waiter/chef/cashier
    // above), so this isn't filling an otherwise-blank screen — just a
    // convenience so the one screen this role actually uses is one click
    // closer.
    else if (user?.role === 'housekeeping') openOrFocusPanel('room-status');
  }, []); // eslint-disable-line

  // Warm up the busiest screens' code in idle time, one at a time, a few
  // seconds after sign-in — so their first click is instant without making
  // the first screen wait (each screen is its own file, lib/lazyWithRetry.js).
  useEffect(() => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
    const timers = [];
    const t = setTimeout(() => {
      PRELOAD_KEYS.forEach((key, i) => {
        timers.push(setTimeout(() => idle(() => getModuleEntry(key)?.component?.preload?.()), i * 1500));
      });
    }, 4000);
    return () => { clearTimeout(t); timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const withoutHash = hash.slice(1);
    const tabName = withoutHash.replace(/-\d+$/, '');
    if (MODULE_REGISTRY.some((m) => m.key === tabName)) {
      openOrFocusPanel(tabName);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const loadData = async () => {
      try {
        // /bookings/stats and /bookings are independent — neither depends
        // on the other's result — but were previously awaited one after
        // another, doubling this page-shell load's network wall-time on
        // EVERY dashboard visit. Fired together instead (found 2026-09-27
        // during a broader performance pass, same fix shape already
        // applied to TablesPOS.js's loadTabs and restaurant.js's
        // loadTabDetail).
        const [statsResponse, bookingsResponse] = await Promise.all([
          apiFetch('/bookings/stats'),
          apiFetch('/bookings'),
        ]);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          const parseKES = (s)=>{ if(!s) return 0; return parseFloat(String(s).replace(/[^\d.]/g,''))||0; };
          const todayRev = parseKES(statsData.today_revenue_formatted || statsData.today_revenue_formatted);
          // last_5_day_revenue is array of {date, iso, revenue}
          const chart = Array.isArray(statsData.last_5_day_revenue)? statsData.last_5_day_revenue.map(d=>({ date: d.date, revenue: d.revenue||0, iso: d.iso })) : [];
          setStats({
            // Revenue fields are superuser-only as of the backend split
            // (worker/lib/bookings.js's handleGetBookingStats) — absent
            // (undefined) in the response for every other role, which the
            // `|| 0` / `|| 'KES 0.00'` fallbacks already handle correctly.
            // hasRevenueAccess distinguishes "revenue is genuinely zero"
            // from "this role can't see revenue at all", so the UI can hide
            // the Today Revenue tile/chart entirely instead of showing a
            // misleading KES 0.00 to roles that just aren't allowed to see it.
            hasRevenueAccess: statsData.today_revenue_formatted !== undefined,
            todayRevenue: todayRev,
            todayRevenueFormatted: statsData.today_revenue_formatted || 'KES 0.00',
            availableRooms: statsData.available_rooms || 0,
            // total_rooms is a real COUNT(*) from getDashboardStats
            // (worker/lib/bookings.js) — 0 is a genuine, correct answer for
            // a brand-new tenant with no rooms yet, not a "missing data"
            // case. `|| 27` previously fell back to the SAWAN Hotel demo
            // tenant's own room count, so every newly-registered hotel saw
            // a misleading "0/27" instead of the honest "0/0".
            totalRooms: statsData.total_rooms ?? 0,
            occupiedRooms: statsData.occupied_rooms || 0,
            totalBookings: statsData.total_bookings || 0,
            revenueChart: chart.reverse() // most recent last, reverse so Today at top
          });
        } else {
          // BUG FIX: a non-ok response (403 for a role outside
          // BOOKING_MANAGEMENT, or any other failure) previously left
          // `stats` as null forever — DashboardContent gates its entire
          // render on `stats` being non-null, so this was a PERMANENT
          // "Loading dashboard..." spinner for chef/waiter (neither role is
          // in BOOKING_MANAGEMENT) with no error, no retry, nothing in the
          // console. Setting a valid empty/zeroed stats object here lets
          // the dashboard actually render for any role this endpoint
          // rejects, present or future.
          setStats({
            hasRevenueAccess: false,
            todayRevenue: 0,
            todayRevenueFormatted: 'KES 0.00',
            availableRooms: 0,
            totalRooms: 0,
            occupiedRooms: 0,
            totalBookings: 0,
            revenueChart: [],
          });
        }

        // Load bookings for active bookings modal
        if (bookingsResponse.ok) {
          const bookingsData = await bookingsResponse.json();
          setBookings(bookingsData.bookings || []);
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        // Same reasoning as the !statsResponse.ok branch above — a network-
        // level failure must not leave the dashboard stuck loading forever.
        setStats((prev) => prev ?? {
          hasRevenueAccess: false,
          todayRevenue: 0,
          todayRevenueFormatted: 'KES 0.00',
          availableRooms: 0,
          totalRooms: 0,
          occupiedRooms: 0,
          totalBookings: 0,
          revenueChart: [],
        });
      }
    };
    
    loadData();
  }, []);

  // ---------------------------------------------------------------------
  // Top-nav mega-menu groups (Phase 2: OPERA Cloud-style shell)
  // ---------------------------------------------------------------------
  // Data-driven nav visibility (2026-09 rewrite): each module key maps to
  // its allowed roles in frontend/src/config/roleModules.js, mirroring the
  // backend's ROLE_GROUPS shape. Replaces the old two-boolean scheme
  // (isSupervisorPlus/isSuperuser), which couldn't scale past the original
  // 4 roles without an ever-growing if/else chain.
  //
  // "Front Desk" and "Point of Sale" hold >1 item only for roles that can
  // see the gated second item (Room Management / POS Admin); for roles
  // that only see the base item, that category collapses to a single
  // plain nav button instead of a one-item dropdown - a one-item mega
  // menu is just a worse button (extra click, no information gained).
  // Role check + per-tenant entitlement check (worker/lib/entitlements.js).
  // While entitlements are still loading, isEnabled() returns true so nav
  // doesn't flash-hide items the tenant actually has.
  const can = (moduleKey) => canSeeModule(user.role, moduleKey, isFeatureEnabled, hasRoleCapability);

  // Unread in-app chat count for the nav badge. Polling pauses while a Chat
  // panel is open at all (that page polls faster on its own) and when the
  // role can't see chat at all. Desktop toasts fire from here so they work
  // regardless of which panels are open/frontmost.
  const { unread: unreadChat } = useUnreadChat({
    active: can('CHAT') && !hasOpenPanelFor('chat'),
    notify: true,
  });

  const frontDeskItems = [];
  if (can('BOOKINGS')) {
    frontDeskItems.push({ id: 'bookings', label: 'Bookings', icon: CalendarCheck });
  }
  if (can('CUSTOMERS')) {
    frontDeskItems.push({ id: 'customers', label: 'Customer Centre', icon: Contact });
  }
  if (can('DOCUMENTS')) {
    frontDeskItems.push({ id: 'documents', label: 'Documents', icon: FileText });
  }
  if (can('CLAIMS')) {
    frontDeskItems.push({ id: 'claims', label: 'Claims', icon: FolderArchive });
  }
  if (can('ROOM_MANAGEMENT')) {
    frontDeskItems.push({ id: 'room-mgmt', label: 'Room Management', icon: DoorOpen });
  }
  // Housekeeping's own room-status view (2026-09-21 fix — see
  // docs/KNOWN_GAPS.md). Deliberately a SEPARATE nav entry from
  // 'room-mgmt', not a shared one — housekeeping has ROOM_STATUS but never
  // ROOM_MANAGEMENT, and a manager/supervisor/superuser who already has the
  // fuller Room Management screen doesn't need a second, narrower one
  // competing for the same menu slot.
  if (can('ROOM_STATUS') && !can('ROOM_MANAGEMENT')) {
    frontDeskItems.push({ id: 'room-status', label: 'Room Status', icon: DoorOpen });
  }
  // Conference & Events — NOT tenant-optional (no entitlement gate, see
  // roleModules.js), placed alongside Bookings/Documents/Room Management
  // since it shares BOOKINGS' exact role audience and is front-of-house
  // sales work, not a POS-family or side-module concept.
  if (can('CONFERENCE')) {
    frontDeskItems.push({ id: 'conference', label: 'Conference & Events', icon: Presentation });
  }
  // Conference meal cards: scanning at the dining hall is open to the serving roles too.
  if (can('MEAL_CARDS')) {
    frontDeskItems.push({ id: 'meal-cards', label: 'Meal Cards', icon: QrCode });
  }
  // Guest Portal: guests' phone page — links to share, breakfast pre-orders, requests.
  if (can('GUEST_PORTAL')) {
    frontDeskItems.push({ id: 'guest-portal', label: 'Guest Portal', icon: Smartphone });
  }

  // Restaurant: the kitchen/stock screens grouped under ONE nav item so the bar
  // stays short (2026-10-09, owner ask: "KISS"). Same collapse rule as Front
  // Desk / POS — 0 items renders nothing, exactly 1 stays a plain button with
  // its own name (a chef who only has Kitchen Display sees "Kitchen Display",
  // not a one-item "Restaurant" menu), >1 becomes the dropdown. Each screen
  // keeps its own role gate; nothing here widens anyone's access.
  const restaurantItems = [];
  if (can('KDS')) restaurantItems.push({ id: 'kds', label: 'Kitchen Display', icon: ChefHat });
  if (can('STOCK_ISSUES')) restaurantItems.push({ id: 'stock-issues', label: 'Stock Issues', icon: ClipboardList });
  if (can('PORTION_STOCK')) restaurantItems.push({ id: 'portion-stock', label: 'Portion Stock', icon: Boxes });
  if (can('KITCHEN_SHEET')) restaurantItems.push({ id: 'kitchen-sheet', label: 'Kitchen Sheet', icon: ClipboardCheck });
  if (can('MEAL_PLAN')) restaurantItems.push({ id: 'meal-plan', label: 'Meal Plan', icon: CalendarDays });

  const posItems = [];
  if (can('POS')) {
    posItems.push({ id: 'pos', label: 'POS', icon: CircleDollarSign });
  }
  // Tables — the restaurant table-service flow (open tabs, fire rounds,
  // request bill, tender payments) on top of the same POS_MANAGEMENT
  // audience as quick-sale POS. Separate page/tab from 'pos' itself
  // (TablesPOS.js) rather than a mode toggle inside POS.js — see the
  // restaurant service-flow project memory's Step 5 note on why: POS.js is
  // a large, working, daily-use file and a bolt-on toggle risked
  // regressing the walk-up quick-sale path.
  if (can('POS')) {
    posItems.push({ id: 'tables', label: 'Tables', icon: Utensils });
  }
  if (can('POS_ADMIN')) {
    posItems.push({ id: 'admin-pos', label: 'POS Admin', icon: ShieldCheck });
  }
  // Water Bottling Wing — tenant-optional module. Requires BOTH the role
  // check (WATER_WING module key) AND the tenant having actually enabled
  // it (tenantBrand.water_wing_enabled, toggled in Settings) — a role that
  // could see this item on an enabled tenant sees nothing extra on a
  // tenant that hasn't turned the module on.
  if (can('WATER_WING') && tenantBrand?.water_wing_enabled) {
    posItems.push({ id: 'water-wing', label: 'Water Wing', icon: Droplets });
  }
  // Spa & Gym — tenant-optional module, same gating shape as Water Wing
  // immediately above.
  if (can('SPA_GYM') && tenantBrand?.spa_gym_enabled) {
    posItems.push({ id: 'spa-gym', label: 'Spa & Gym', icon: Sparkles });
  }

  const managementItems = [];
  if (can('REPORTS')) {
    managementItems.push({ id: 'reports', label: 'Reports', icon: FileBarChart });
  }
  if (can('PROCUREMENT')) {
    // Procurement's own tabs have mixed backend gates (suppliers/POs/
    // receiving: superuser+supervisor; requisitions: +manager+chef;
    // payments/debit-notes: superuser only) — the nav item itself is
    // visible to the broadest audience (supervisor+/chef) the page
    // contains; a role attempting a tab beyond their own access gets a
    // clean 403 from the backend, matching how every other mixed-gate
    // page in this app already behaves. Procurement.js itself must further
    // hide/show its internal tabs per role.
    managementItems.push({ id: 'procurement', label: 'Procurement', icon: Truck });
  }
  if (can('RECEIPTS')) {
    // Scan supplier receipts into stock + the month's input VAT. Super admin
    // plus managers, who get the limited view (owner, 2026-11-02) — backend PURCHASE_RECEIPTS_SCAN is locked.
    managementItems.push({ id: 'purchase-receipts', label: 'Purchase Receipts', icon: ScanLine });
  }
  if (can('TAX_HEALTH')) {
    // eTIMS on purchases, possible savings, staffing guide, saved reports.
    managementItems.push({ id: 'tax-health', label: 'Tax Health', icon: HeartPulse });
  }
  if (can('STAFF')) {
    managementItems.push({ id: 'staff', label: 'Staff', icon: Users });
  }
  if (can('USER_MANAGEMENT')) {
    managementItems.push({ id: 'users', label: 'User Management', icon: UserCog });
  }
  if (can('ROLE_MANAGEMENT')) {
    managementItems.push({ id: 'roles', label: 'Role Management', icon: ShieldCheck });
  }
  if (can('ACCOUNTING')) {
    // Financial statements are gated FINANCIAL_MANAGEMENT (superuser-only)
    // on the backend — see worker/lib/auth.js. Nav visibility mirrors that
    // exactly, same as every other superuser-only item in this list.
    managementItems.push({ id: 'accounting', label: 'Accounting', icon: Landmark });
  }
  if (can('LEDGER_POSTING')) {
    // Delayed posting: manager+ reviews and posts the takings waiting in the
    // holding area; the settings inside are superuser-only (backend-enforced).
    managementItems.push({ id: 'post-the-day', label: 'Post the Day', icon: ClipboardCheck });
  }
  if (can('LEDGER_ADJUSTMENTS')) {
    // Real "superuser" role only, on the backend AND here — no
    // MODULE_CAPABILITY override exists for this key (see roleModules.js's
    // own comment on it), unlike ACCOUNTING's FINANCIAL_MANAGEMENT one. A
    // junior admin role, however it gets customized later, can never gain
    // visibility into this.
    managementItems.push({ id: 'ledger-adjustments', label: 'Ledger Adjustments', icon: ShieldAlert });
  }
  if (can('BUDGETS')) {
    managementItems.push({ id: 'budgets', label: 'Budgets', icon: Target });
  }
  if (can('PETTY_CASH')) {
    // Day-to-day custody is supervisor+ (PETTY_CASH_MANAGEMENT); tin setup
    // and cost-centre CRUD inside the page are manager+ (PETTY_CASH_SETUP),
    // enforced by the backend and hidden in PettyCash.js for a supervisor.
    managementItems.push({ id: 'petty-cash', label: 'Petty Cash', icon: Wallet });
  }
  if (can('ASSET_REGISTER')) {
    // What the hotel owns, per room/area. Housekeeping and the desk see it and flag
    // broken/missing pieces; adding, moving and retiring is supervisor+ (inside the page).
    managementItems.push({ id: 'asset-register', label: 'Asset Register', icon: Armchair });
  }
  if (can('CORPORATE_CLIENTS')) {
    // Manager+ only (CORPORATE_CLIENT_MANAGEMENT) — creating/editing the
    // company accounts a booking can be billed to. Selecting an existing
    // one on the booking form itself is BOOKING_MANAGEMENT, unchanged.
    managementItems.push({ id: 'corporate-clients', label: 'Corporate Clients', icon: Building2 });
  }

  // Interactive handlers for dashboard
  const handleActiveBookingsClick = () => {
    setShowActiveBookings(true);
  };

  const handleRevenueClick = async () => {
    try {
      const response = await apiFetch('/bookings/stats');
      if (response.ok) {
        const data = await response.json();
        setRevenueDetails({
          total_revenue: data.total_revenue_formatted || 'KES 0.00',
          occupied_rooms: data.occupied_rooms || 0,
          total_bookings: data.total_bookings || 0,
          occupancy_rate: data.occupancy_rate || 0,
          available_rooms: data.available_rooms || 0,
          total_rooms: data.total_rooms ?? 0 // see the loadData effect above for why this isn't || 27
        });
        setShowRevenueBreakdown(true);
      }
    } catch (err) {
      console.error('Failed to load revenue details:', err);
    }
  };

  const handleGuestClick = (booking) => {
    setSelectedBooking(booking);
    setShowGuestDetails(true);
  };

  // Right-click-to-open-in-new-tab affordance (decision, Phase 2):
  // PRESERVED, not dropped. The original sidebar buttons opened a new
  // browser tab on right-click via a timestamped hash so staff could keep
  // e.g. the Dashboard open in one tab and POS in another. That workflow
  // is still valuable in the new top-nav shell (arguably more so, since
  // fewer destinations are one click away now that some live inside a
  // dropdown), so it's carried over unchanged onto every leaf nav item -
  // both the plain top-level buttons (Dashboard/Inventory when they
  // collapse to a single destination) and the items inside each
  // NavDropdown (via NavDropdown's new optional `onContextMenu` passthrough,
  // see components/NavDropdown.js). Right-click on a closed dropdown's
  // trigger button does nothing new (same as before: the old sidebar had
  // no per-category header to right-click either, only leaf items).
  const openInNewTab = (tabId) => {
    const hash = `#${tabId}-${Date.now()}`;
    window.open(`${window.location.origin}${window.location.pathname}${hash}`, '_blank');
  };
  const handleNavContextMenu = (tabId) => (e) => {
    e.preventDefault();
    openInNewTab(tabId);
  };

  return (
    // Viewport-locked app shell (not a scrolling document) — see
    // src/index.css's "App shell viewport model" comment for the full
    // rationale, and its .app-shell-root rule for why the actual height
    // (100dvh with a 100vh fallback, not min-h-screen/h-screen) is set
    // there rather than as a Tailwind h-dvh utility class here. `dvh`
    // tracks the browser's ACTUAL visible viewport, correctly accounting
    // for mobile browser chrome that shows/hides on scroll (100vh on iOS
    // Safari is the height with the address bar hidden, which clips
    // content behind it when the bar is shown — dvh has no such gap).
    // overflow-hidden here is deliberate and safe, not
    // overflow-hidden-as-a-hack: this outer div is ONLY ever exactly one
    // viewport tall by construction, so nothing legitimate can ever need
    // to overflow it — TopNavBar is a fixed h-14, and the content region
    // below is the one designated scroll container for anything taller
    // than the remaining space.
    <div className="app-shell-root tenant-theme-root flex flex-col overflow-hidden bg-gray-50">
      {/* Shown only when this session is a TALIT platform support session
          (impersonate / shadow / full-access). Renders nothing otherwise.
          See docs/platform-admin-architecture.md §4. */}
      <ImpersonationBanner />
      <PlatformAnnouncementBanner />
      {/* Guest orders / requests: stays until someone clicks it (see the component). */}
      <GuestAlertBanner onOpenTab={focusPanel} />
      <TopNavBar
        brand={
          <span className="flex items-center gap-2 text-shell-text" title="TALIT PMS">
            {tenantBrand?.logo_url ? (
              <img
                src={resolveAssetUrl(tenantBrand.logo_url)}
                alt={`${tenantBrand.name} logo`}
                className="w-6 h-6 object-contain rounded-sm bg-white/10"
              />
            ) : (
              <Hotel className="w-6 h-6" aria-hidden="true" />
            )}
            <span className="font-bold text-lg tracking-tight truncate max-w-[8.5rem] sm:max-w-none">
              {tenantBrand?.name || 'TALIT PMS'}
            </span>
          </span>
        }
        right={
          <div className="flex items-center gap-2">
            {/* Shift clock-in/out — mirrors worker/lib/auth.js's
                SHIFT_TRACKING group exactly (frontdesk/waiter/chef/cashier
                only; superuser/manager/supervisor don't clock in/out). The
                backend route gate is the real enforcement; this check just
                keeps the widget off screens that would only ever 403. */}
            {['frontdesk', 'waiter', 'chef', 'cashier'].includes(user?.role) && (
              <ShiftClockWidget user={user} />
            )}
            {/* In-tenant notification bus (migration 0021). Every role can
                hold notifications — visibility is decided per row by the
                backend — so the bell shows for everyone. */}
            {/* Ask TALIT — the AI assistant, one tap from every screen (same
                place MamasLedger puts its assistant: a sparkle beside the
                header icons). Shown to anyone who may chat or read the reports. */}
            {/* Owner decision 2026-10-12: the superuser and the owner only — no one
                else — and a labelled button, not a bare icon, so TALIT stands out. */}
            {['superuser', 'owner'].includes(user?.role) && (can('TALIT_REPORTS') || can('TALIT_CHAT')) && (
              <button
                type="button"
                onClick={() => openOrFocusPanel('talit')}
                title="TALIT, your AI assistant: ask questions, business reports, recipe tuning"
                aria-label="Open TALIT"
                className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full text-sm font-semibold tracking-wide transition-colors border ${
                  hasOpenPanelFor('talit')
                    ? 'bg-white text-shell border-white'
                    : 'bg-white/15 text-white border-white/40 hover:bg-white hover:text-shell'
                }`}
              >
                <Sparkles size={16} />
                <span>TALIT</span>
              </button>
            )}
            <NotificationBell
              onOpen={focusPanel}
              onSeeAll={() => openOrFocusPanel('notifications')}
            />
            <UserMenu
              name={user.name}
              role={myTitle || myRoleLabel || user.role}
              items={[
                { label: 'Log out', icon: LogOut, onClick: onLogout },
              ]}
            />
          </div>
        }
      >
        {/* Dashboard: the permanent home view behind the floating panels
            (see DashboardContent below) — always rendered, never itself a
            panel, so there's nothing to "open." Clicking this instead
            minimizes every open panel, a "show desktop" action that
            surfaces the home view without closing/losing any panel's
            state — the MDI equivalent of what this button used to do
            (switch the single visible tab back to 'dashboard'). Gated on
            can('DASHBOARD') as of 2026-09-21 — waiter/chef already had no
            home view (DashboardContent's own render gate below), and
            cashier lost it in the same change: neither role has any use
            for hotel-occupancy/revenue stats, their whole job is one
            selling screen. */}
        {can('DASHBOARD') && (
          <button
            type="button"
            onClick={() => zOrder.forEach((id) => minimizePanel(id))}
            title="Minimize all panels to see the dashboard"
            className="flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors text-shell-text hover:bg-shell-dark"
          >
            <span>Dashboard</span>
          </button>
        )}

        {/* Front Desk: Bookings/Documents/Room Management, whichever this
            role can see. 0 items renders nothing (e.g. waiter, chef,
            water_assistant have no front-desk access at all), 1 item
            collapses to a plain button, >1 becomes a dropdown. */}
        {frontDeskItems.length > 1 && (
          <NavDropdown
            label="Front Desk"
            items={frontDeskItems.map(item => ({
              label: item.label,
              icon: item.icon,
              onClick: () => openOrFocusPanel(item.id),
              onContextMenu: handleNavContextMenu(item.id),
            }))}
          />
        )}
        {frontDeskItems.length === 1 && (
          <button
            type="button"
            onClick={() => openOrFocusPanel(frontDeskItems[0].id)}
            onContextMenu={handleNavContextMenu(frontDeskItems[0].id)}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor(frontDeskItems[0].id) ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>{frontDeskItems[0].label}</span>
          </button>
        )}

        {/* Point of Sale: POS / POS Admin / Water Wing, whichever this role
            (and, for Water Wing, this tenant's enabled modules) allow. 0
            items renders nothing (e.g. chef, storekeeper, housekeeping have
            no POS access), 1 collapses to a plain button, >1 is a dropdown. */}
        {posItems.length > 1 && (
          <NavDropdown
            label="POS"
            items={posItems.map(item => ({
              label: item.label,
              icon: item.icon,
              onClick: () => openOrFocusPanel(item.id),
              onContextMenu: handleNavContextMenu(item.id),
            }))}
          />
        )}
        {posItems.length === 1 && (
          <button
            type="button"
            onClick={() => openOrFocusPanel(posItems[0].id)}
            onContextMenu={handleNavContextMenu(posItems[0].id)}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor(posItems[0].id) ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>{posItems[0].label}</span>
          </button>
        )}

        {/* Inventory: plain button, only for roles the backend's
            INVENTORY_READ_ROLES actually allows (previously shown to every
            role including frontdesk despite frontdesk never having had
            backend access — a latent mismatch fixed here as part of this
            rewrite, same as the Reports fix above). */}
        {can('INVENTORY') && (
          <button
            type="button"
            onClick={() => openOrFocusPanel('inventory')}
            onContextMenu={handleNavContextMenu('inventory')}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor('inventory') ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>Inventory</span>
          </button>
        )}

        {/* Recipes: plain button — a narrow door for `chef` (plus
            supervisor/manager/superuser) to maintain ingredient lists
            without full POS admin access. See frontend/src/config/
            roleModules.js's RECIPES key and worker/lib/auth.js's
            POS_RECIPE_MANAGEMENT group. */}
        {can('RECIPES') && (
          <button
            type="button"
            onClick={() => openOrFocusPanel('recipes')}
            onContextMenu={handleNavContextMenu('recipes')}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor('recipes') ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>Recipes</span>
          </button>
        )}

        {/* Shift Report: self-service Z-out/X-out for the four "working"
            roles (frontdesk/waiter/chef/cashier). Plain button, same
            treatment as Recipes/KDS — this is self-service, not oversight
            tooling, so it doesn't belong in the management dropdown. See
            frontend/src/config/roleModules.js's SHIFT_REPORT key and
            worker/lib/auth.js's SHIFT_TRACKING group. */}
        {can('SHIFT_REPORT') && (
          <button
            type="button"
            onClick={() => openOrFocusPanel('shift-report')}
            onContextMenu={handleNavContextMenu('shift-report')}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor('shift-report') ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>Shift Report</span>
          </button>
        )}

        {/* Restaurant: Kitchen Display / Stock Issues / Portion Stock /
            Kitchen Sheet, whichever this role can see (see restaurantItems).
            These used to be four separate buttons; Stock Issues is still a
            self-service screen for every role (STOCK_ISSUE_REQUEST) — it is
            only the menu position that changed. */}
        {restaurantItems.length > 1 && (
          <NavDropdown
            label="Restaurant"
            items={restaurantItems.map(item => ({
              label: item.label,
              icon: item.icon,
              onClick: () => openOrFocusPanel(item.id),
              onContextMenu: handleNavContextMenu(item.id),
            }))}
          />
        )}
        {restaurantItems.length === 1 && (
          <button
            type="button"
            onClick={() => openOrFocusPanel(restaurantItems[0].id)}
            onContextMenu={handleNavContextMenu(restaurantItems[0].id)}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor(restaurantItems[0].id) ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>{restaurantItems[0].label}</span>
          </button>
        )}

        {can('DAILY_MANAGEMENT_REPORT') && (
          <button
            type="button"
            onClick={() => openOrFocusPanel('daily-management-report')}
            onContextMenu={handleNavContextMenu('daily-management-report')}
            title="Daily Hotel Sales & Collection — right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor('daily-management-report') ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>Daily Report</span>
          </button>
        )}

        {/* Settings: hotel name/logo. Viewing is allowed for most staff —
            editing is gated to superuser inside the Settings page itself,
            not at the nav level. Gated on can('SETTINGS') as of 2026-09-21:
            waiter/cashier removed (explicit user decision — their whole job
            is one selling screen, no use for hotel-wide settings). */}
        {can('SETTINGS') && (
        <button
          type="button"
          onClick={() => openOrFocusPanel('settings')}
          onContextMenu={handleNavContextMenu('settings')}
          title="Right-click to open in new tab"
          className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
            hasOpenPanelFor('settings') ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
          }`}
        >
          <span>Settings</span>
        </button>
        )}

        {/* Chat: in-app staff messaging (migration 0020). Plain button for
            every role — the unread badge shows the current count from
            useUnreadChat. */}
        {can('CHAT') && (
          <button
            type="button"
            onClick={() => openOrFocusPanel('chat')}
            onContextMenu={handleNavContextMenu('chat')}
            title="Right-click to open in new tab"
            className={`relative flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor('chat') ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>Chat</span>
            {unreadChat > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs font-semibold rounded-full px-1.5 min-w-[18px] text-center leading-tight">
                {unreadChat > 99 ? '99+' : unreadChat}
              </span>
            )}
          </button>
        )}

        {/* Management: Reports (supervisor+), Staff + User Management
            (superuser only). 0 items for base roles (rendered as nothing),
            1 item (supervisor: Reports only) collapses to a plain button,
            3 items (superuser) is a real dropdown. */}
        {managementItems.length > 1 && (
          <NavDropdown
            label="Management"
            align="right"
            items={managementItems.map(item => ({
              label: item.label,
              icon: item.icon,
              onClick: () => openOrFocusPanel(item.id),
              onContextMenu: handleNavContextMenu(item.id),
            }))}
          />
        )}
        {managementItems.length === 1 && (
          <button
            type="button"
            onClick={() => openOrFocusPanel(managementItems[0].id)}
            onContextMenu={handleNavContextMenu(managementItems[0].id)}
            title="Right-click to open in new tab"
            className={`flex items-center px-2.5 py-2 rounded text-sm font-medium transition-colors ${
              hasOpenPanelFor(managementItems[0].id) ? 'bg-shell-dark text-shell-text' : 'text-shell-text hover:bg-shell-dark'
            }`}
          >
            <span>{managementItems[0].label}</span>
          </button>
        )}
      </TopNavBar>
      {/* Main Content — the ONE designated scroll container in the shell.
          flex-1 + min-h-0 is the load-bearing pair: flex-1 alone lets this
          region GROW to fill remaining space, but a flex child's default
          min-height is `auto` (= its content's natural height), so without
          min-h-0 a tall page below would still force this region — and the
          h-dvh ancestor above it — to grow past the viewport, reintroducing
          the exact document-scroll bug this shell exists to prevent.
          min-h-0 caps it at the flex-allocated space instead, and
          overflow-y-auto turns any excess into a scrollbar CONTAINED to
          this region — the nav bar above stays pinned, only content
          scrolls. relative: positions the watermark below. */}
      {/* MDI content region — a relative, non-scrolling wrapper spanning
          exactly the space below TopNavBar, holding two SIBLING layers:
          .app-shell-scroll (the home dashboard, its own scroll container,
          unchanged from before this feature) and PanelDesktop (the
          floating-panel layer, absolutely positioned on top of it). Panels
          are deliberately NOT nested inside .app-shell-scroll's own
          overflow-y-auto — dragging/resizing a panel must never interact
          with that container's scroll behavior; each panel manages its own
          internal scrolling instead (see PanelWindow.js). */}
      <div className="relative flex-1 min-h-0">
      <div className="app-shell-scroll relative h-full overflow-y-auto">
        {/* Whole-app-shell watermark (user request, 2026-08-26): the tenant's
            own logo, large and extremely faint, centered behind the content
            on EVERY tab - not just Dashboard - for a persistent "branded
            everywhere" feel without ever competing with readability. Lives on
            this single shared wrapper (not per-tab) so it's one definition
            for the whole shell, matching how the user described it as a
            background rather than something each page renders individually.
            opacity-[0.06] deliberately much fainter than the user's own
            reference screenshot (they explicitly asked for 50-75% MORE faded
            than that example) - bg-contain + bg-center keeps it from
            distorting or tiling regardless of the source image's aspect
            ratio. pointer-events-none so it never intercepts clicks; aria-
            hidden since it's purely decorative. Renders nothing when no logo
            has been uploaded yet (tenantBrand?.logo_url is null).
            fixed (not absolute) so it stays pinned to the viewport rather
            than scrolling away with content now that this wrapper is
            scrollable — matches how a watermark reads in every desktop app
            that has one (Word, Excel, ...): always visible behind whatever
            you're looking at, never something you scroll past. */}
        {tenantBrand?.logo_url && (
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 top-14 bg-center bg-no-repeat bg-contain opacity-[0.06]"
            style={{ backgroundImage: `url(${resolveAssetUrl(tenantBrand.logo_url)})`, backgroundSize: 'min(60%, 32rem)' }}
          />
        )}
        <div className="relative p-8">
  {/* DashboardContent is the permanent home view behind the floating
      panels — always rendered, never itself a panel (see the "Dashboard"
      nav button above, which minimizes panels rather than "opening" this).
      Matches QuickBooks Desktop's own shape: a non-closable home screen
      behind whatever windows are open, not a window itself.

      EXCEPT for waiter/chef/cashier (2026-09 nav cleanup; cashier added
      2026-09-21, explicit user decision): none of the three has any use
      for hotel-occupancy/revenue stats, so their home view is just
      whatever module panel is open — see the auto-focus effect above,
      which opens one on first login so this never shows a genuinely blank
      screen instead. */}
  {!['waiter', 'chef', 'cashier'].includes(user?.role) && (
    <DashboardContent
      stats={stats}
      bookings={bookings}
      user={user}
      hotelName={tenantBrand?.name}
      can={can}
      onNavigateToBookings={() => openOrFocusPanel('bookings')}
      onActiveBookingsClick={handleActiveBookingsClick}
      onRevenueClick={handleRevenueClick}
      onOpenTab={focusPanel}
    />
  )}
        </div>
      </div>
      <PanelDesktop
        panels={panels}
        zOrder={zOrder}
        moduleIcons={MODULE_ICONS}
        renderPanelContent={(panel, isVisible) => (
          <PanelHost panel={panel} user={user} active={isVisible} onFocusPanel={focusPanel} />
        )}
        onFocus={bringToFront}
        onMove={movePanel}
        onResize={resizePanel}
        onMinimize={minimizePanel}
        onRestore={restorePanel}
        onToggleMaximize={toggleMaximize}
        onClose={closePanel}
        onNewWindow={openNewInstance}
      />
      </div>

      {/* Active Bookings Modal */}
      {showActiveBookings && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Active Bookings ({bookings.length})</h3>
              <button
                onClick={() => setShowActiveBookings(false)}
                className="text-gray-600 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            {bookings.length === 0 ? (
              <div className="text-center py-8 text-gray-700">No active bookings found</div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {bookings.map(booking => (
                  <div key={booking.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4
                          className="font-semibold text-gray-800 hover:text-blue-600 cursor-pointer transition-colors flex items-center gap-1.5"
                          onClick={e => { e.stopPropagation(); handleGuestClick(booking); }}
                        >
                          {booking.guest_name}
                          <Users className="w-4 h-4" aria-hidden="true" />
                        </h4>
                        <p className="text-sm text-gray-600">Room {booking.room_number} - {booking.room_type}</p>
                        <p className="text-sm text-gray-600">{new Date(booking.check_in).toLocaleDateString()} - {booking.open_ended ? 'Open stay' : new Date(booking.check_out).toLocaleDateString()}</p>
                        <p className="text-sm font-medium text-green-600">{booking.total_amount_formatted}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                        booking.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {idPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow p-4 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
            <button onClick={()=>setIdPreview('')} className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-sm">✕</button>
            <h4 className="font-semibold text-gray-800 mb-3 text-sm">Attached ID Preview</h4>
            {idPreview.startsWith('data:image') ? (
              <img src={idPreview} alt="Guest ID" className="max-h-[70vh] object-contain mx-auto" />
            ) : (
              <iframe src={idPreview} title="ID Document" className="w-full h-[70vh]" />
            )}
          </div>
        </div>
      )}

      {/* Revenue Breakdown Modal */}
      {showRevenueBreakdown && revenueDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Today Revenue Breakdown</h3>
              <button
                onClick={() => setShowRevenueBreakdown(false)}
                className="text-gray-600 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 p-3 rounded-lg">
                <h4 className="font-semibold text-green-800">Total Revenue</h4>
                <p className="text-2xl font-bold text-green-600">{revenueDetails.total_revenue}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-blue-800">Occupied Rooms</h4>
                  <p className="text-xl font-bold text-blue-600">{revenueDetails.occupied_rooms}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-purple-800">Total Bookings</h4>
                  <p className="text-xl font-bold text-purple-600">{revenueDetails.total_bookings}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-800">Occupancy Rate</h4>
                  <p className="text-xl font-bold text-orange-600">{revenueDetails.occupancy_rate}%</p>
                </div>
                <div className="bg-teal-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-teal-800">Available Rooms</h4>
                  <p className="text-xl font-bold text-teal-600">{revenueDetails.available_rooms}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Guest Details Modal (structured) */}
      {showGuestDetails && selectedBooking && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow p-6 w-full max-w-sm text-sm max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-base font-semibold text-gray-800">Guest Details</h3>
              <button
                onClick={() => setShowGuestDetails(false)}
                className="text-gray-700 hover:text-gray-700 text-lg leading-none"
                aria-label="Close guest details"
              >×</button>
            </div>

            <div className="space-y-4">
              {/* Identity */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">Full Name</p>
                <p className="font-medium">{selectedBooking.guest_name || 'N/A'}</p>
              </div>

              {/* Contact grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">Email</p>
                  <p>{selectedBooking.guest_email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">Phone</p>
                  <p>{selectedBooking.guest_phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">National ID</p>
                  <p>{selectedBooking.guest_national_id || selectedBooking.national_id || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">Address</p>
                  <p className="truncate" title={selectedBooking.guest_address}>{selectedBooking.guest_address || 'N/A'}</p>
                </div>
              </div>

              {/* Attached ID */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">Attached ID</p>
                {selectedBooking.guest_id_document || selectedBooking.id_document ? (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-xs font-medium">Provided</span>
                    <button
                      onClick={()=> setIdPreview(selectedBooking.guest_id_document || selectedBooking.id_document)}
                      className="text-blue-600 hover:underline text-xs"
                    >View</button>
                  </div>
                ) : (
                  <p className="text-gray-600">Not Provided</p>
                )}
              </div>

              {/* Booking summary */}
              <div className="border-t pt-3">
                <p className="text-xs uppercase tracking-wide text-gray-700 mb-1">Booking Summary</p>
                <div className="space-y-1">
                  <p>Room <span className="font-medium">{selectedBooking.room_number}</span> – {selectedBooking.room_type}</p>
                  <p>Check-in: {new Date(selectedBooking.check_in).toLocaleDateString()}</p>
                  <p>Check-out: {selectedBooking.open_ended ? 'Open stay' : new Date(selectedBooking.check_out).toLocaleDateString()}</p>
                  <p>Guests: {selectedBooking.number_of_adults} Adult(s){selectedBooking.number_of_children>0?`, ${selectedBooking.number_of_children} Child(ren)`:''}</p>
                  <p>Status: <span className="text-xs font-semibold capitalize px-2 py-0.5 rounded-full bg-gray-200 text-gray-800">{selectedBooking.status}</span></p>
                  <p>Total: <span className="font-semibold text-green-600">{selectedBooking.total_amount_formatted}</span></p>
                  {selectedBooking.special_requests && <p>Requests: {selectedBooking.special_requests}</p>}
                  {selectedBooking.confirmation_code && <p>Code: <span className="font-mono text-xs">{selectedBooking.confirmation_code}</span></p>}
                </div>
              </div>
            </div>

            <div className="mt-6 text-right">
              <button
                onClick={()=>setShowGuestDetails(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-1.5 rounded text-xs"
              >Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The Data Integrity dashboard card (unbalanced journal entries, negative
// stock, low-stock alerts) was removed 2026-09-22 per the owner's explicit
// request — it was cluttering the dashboard landing page, especially once
// the freshly-populated inventory catalog started throwing many routine
// low-stock notices at once. Nothing is lost: every one of these alerts is
// still fully visible in the Notifications bell/tab (worker/lib/
// tenantIntegrity.js still produces them into the same 0021 notification
// bus) — this only removes the DUPLICATE surface on the dashboard.

const QUICK_KEYS = ['bookings', 'room-status', 'pos', 'tables', 'kitchen-sheet', 'inventory', 'reports', 'accounting', 'documents', 'customers'];

function greetingFor(date) {
  const h = date.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

// Home screen (restyled 2026-11-03). Everything shown is worked out from the
// stats and bookings this page already loads; the tiles open the same panels
// and modals they always did.
function DashboardContent({ stats, bookings, user, hotelName, can, onNavigateToBookings, onActiveBookingsClick, onRevenueClick, onOpenTab }) {
  if (!stats) {
    return <div className="animate-pulse">Loading dashboard...</div>;
  }
  const now = new Date();
  const today = localToday();
  const desk = frontDeskSummary(bookings, [], today);
  const occupancy = stats.totalRooms ? Math.round((stats.occupiedRooms / stats.totalRooms) * 100) : 0;
  const firstName = String(user?.name || user?.username || '').split(' ')[0];
  const chart = stats.revenueChart || [];
  const maxRev = Math.max(1, ...chart.map((d) => d.revenue || 0));
  const quick = QUICK_KEYS.filter((k) => can(k) && MODULE_ICONS[k]);
  const tiles = [
    stats.hasRevenueAccess && { label: "Today's revenue", value: stats.todayRevenueFormatted || `KES ${stats.todayRevenue.toLocaleString()}`, icon: CircleDollarSign, tone: 'green', hint: 'See the breakdown', onClick: onRevenueClick },
    { label: 'Available rooms', value: `${stats.availableRooms}/${stats.totalRooms}`, icon: BedDouble, tone: 'blue', hint: 'Open bookings', onClick: onNavigateToBookings },
    { label: 'Active bookings', value: stats.totalBookings, icon: CalendarCheck, tone: 'purple', hint: 'See who is booked', onClick: onActiveBookingsClick },
    { label: 'Occupancy', value: `${occupancy}%`, icon: TrendingUp, tone: 'amber', hint: `${stats.occupiedRooms} of ${stats.totalRooms} rooms`, bar: occupancy },
  ].filter(Boolean);
  const deskGroups = [
    ['Arrivals today', desk.arrivals, 'green'],
    ['Departures today', desk.departures, 'red'],
    ['In house', desk.inHouse, 'blue'],
  ];
  return (
    <div className="dh">
      <header className="dh-hero">
        <div>
          <h1>{greetingFor(now)}{firstName ? `, ${firstName}` : ''}</h1>
          <p>{now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}{hotelName ? ` · ${hotelName}` : ''}</p>
        </div>
        {can('bookings') && (
          <button type="button" className="dh-cta" onClick={onNavigateToBookings}>
            <CalendarCheck size={18} aria-hidden="true" /> Open reservations
          </button>
        )}
      </header>

      <div className="dh-kpis">
        {tiles.map(({ label, value, icon: Icon, tone, hint, onClick, bar }) => {
          const inner = (
            <>
              <span className="dh-kpi-icon"><Icon size={22} aria-hidden="true" /></span>
              <span className="dh-kpi-body">
                <span className="dh-kpi-label">{label}</span>
                <strong>{String(value).startsWith('KES ') ? <><small>KES</small>{String(value).slice(3)}</> : value}</strong>
                <span className="dh-kpi-hint">{hint}</span>
                {bar !== undefined && <span className="dh-meter" aria-hidden="true"><i style={{ width: `${Math.min(100, bar)}%` }} /></span>}
              </span>
            </>
          );
          return onClick
            ? <button type="button" key={label} onClick={onClick} className={`dh-card dh-kpi dh-tone-${tone}`}>{inner}</button>
            : <div key={label} className={`dh-card dh-kpi dh-tone-${tone}`}>{inner}</div>;
        })}
      </div>

      <div className="dh-grid">
        <section className="dh-card dh-desk" aria-label="Today at the front desk">
          <h2>Today at the front desk</h2>
          <div className="dh-desk-cols">
            {deskGroups.map(([title, list, tone]) => (
              <div key={title} className={`dh-desk-col dh-tone-${tone}`}>
                <h3><span>{title}</span><b>{list.length}</b></h3>
                {list.length === 0 ? <p className="dh-empty">Nobody</p> : (
                  <ul>
                    {list.slice(0, 5).map((b) => (
                      <li key={b.id}><button type="button" onClick={onNavigateToBookings}><strong>{b.guest_name}</strong><span>Room {b.room_number}</span></button></li>
                    ))}
                    {list.length > 5 && <li className="dh-more">+ {list.length - 5} more</li>}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        {stats.hasRevenueAccess && (
          <section className="dh-card dh-revenue" aria-label="Revenue trend">
            <h2>Revenue, last days</h2>
            {chart.length === 0 ? <p className="dh-empty">No revenue recorded yet.</p> : (
              <ol className="dh-bars">
                {[...chart].reverse().map((day) => (
                  <li key={day.date} title={`${day.date}: KES ${day.revenue.toLocaleString()}`}>
                    <span className="dh-bar-val">{day.revenue ? day.revenue.toLocaleString() : '0'}</span>
                    <span className="dh-bar"><i style={{ height: `${Math.max(3, Math.round((day.revenue / maxRev) * 100))}%` }} /></span>
                    <span className="dh-bar-day">{day.date}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        )}
      </div>

      {stats.hasRevenueAccess && <TodaySpendCard onOpenTab={onOpenTab} />}

      {quick.length > 0 && (
        <section aria-label="Quick access" className="dh-quick-wrap">
          <h2 className="dh-section">Quick access</h2>
          <div className="dh-quick">
            {quick.map((k) => {
              const Icon = MODULE_ICONS[k];
              const label = (MODULE_REGISTRY.find((m) => m.key === k) || {}).label || k;
              return (
                <button type="button" key={k} className="dh-card dh-quick-tile" onClick={() => onOpenTab(k)}>
                  <span className="dh-quick-icon"><Icon size={20} aria-hidden="true" /></span>
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// "Today's Spend" (2026-10-01 owner ask) — "Sawan can just click to see
// day's spend, and see the purchases, fuel, etc." Self-contained: fetches
// its own data (GET /reports/daily-spend/today, worker/lib/dailySpend.js)
// independently of the main /dashboard/stats payload, same reasoning
// every other dashboard card on this page already follows — one slow/
// failing widget never blocks the rest of the dashboard from rendering.
// Gated on hasRevenueAccess at the call site, same tier as the Revenue
// Trends card right below it — spend is financial detail, not something
// every role should see on their home screen.
function TodaySpendCard({ onOpenTab }) {
  const [spend, setSpend] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/reports/daily-spend/today')
      .then((r) => r.json())
      .then((d) => { if (d.success) setSpend(d); else setError(true); })
      .catch(() => setError(true));
  }, []);

  if (error) return null; // a role without INVENTORY_READ gets a quiet 403 — no broken-looking card
  if (!spend) {
    return (
      <div className="dh-card dh-spend animate-pulse">
        <div className="h-5 w-32 bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenTab && onOpenTab('inventory')}
      className="dh-card dh-spend dh-spend-btn"
    >
      <div className="dh-spend-row">
        <span className="dh-kpi-icon dh-tone-amber"><Fuel size={22} aria-hidden="true" /></span>
        <div className="dh-spend-body">
          <span className="dh-kpi-label">Today's spend</span>
          <strong>KES {(spend.total_cents / 100).toLocaleString()}</strong>
          <span className="dh-spend-detail">
            Purchases KES {(spend.purchases_cents / 100).toLocaleString()}
            {' · '}Petty cash KES {(spend.petty_cash_cents / 100).toLocaleString()}
            {spend.fuel_cents > 0 && <> · Fuel KES {(spend.fuel_cents / 100).toLocaleString()}</>}
          </span>
        </div>
        <span className="dh-spend-go">Open inventory →</span>
      </div>
    </button>
  );
}

export default Dashboard;
